/**
 * The piston engine: a four-cylinder Lycoming, made out of arithmetic.
 *
 * ```
 *                     ┌─ ×1 ─► osc₁.frequency
 *   f₀ (constant) ────┼─ ×2 ─► osc₂.frequency          osc₁…osc₈ ─► harmonic gains ─┐
 *                     └─ ×n ─► oscₙ.frequency                                        │
 *                                                                                    ▼
 *   white noise ─► intake bandpass ─► intake gain ────────────────────────────► soft clip
 *                                                                                    │
 *   white noise ─► slap bandpass ─► [AM by blade-pass pulses] ─► slap gain ─────┐     │
 *                                                                              ▼     ▼
 *   starter: saw + noise ─► starter gain ──────────────────────────────────► lope AM ─► out
 * ```
 *
 * ## The four layers, and what each one is for
 *
 * **Harmonics.** A four-stroke four-cylinder fires twice per crankshaft revolution, so its
 * fundamental is `rpm/30` — 23 Hz at idle, 90 Hz at redline. Eight sine harmonics of that,
 * balanced by {@link harmonicGain}, are the body of the sound. They are driven from a single
 * `ConstantSourceNode` fanned out through fixed multiplier gains rather than from eight separate
 * frequency writes, which is what keeps them exactly in tune with one another: eight independent
 * writes drift by a fraction of a hertz and eight partials a fraction of a hertz apart is a
 * chorus, not an engine.
 *
 * **Lope.** At low RPM you hear individual firing events, because no two cylinders make the same
 * power. That is an amplitude modulation at the *cycle* rate — `rpm/120`, since a four-stroke
 * needs two revolutions to fire everything once — and it is 5.8 Hz at a 700 rpm idle, which is
 * exactly the chug of an aeroplane on the ramp. {@link lopeDepth} fades it out as the RPM comes
 * up, so the same voice loping on the apron is drilling smoothly at cruise.
 *
 * **Intake.** Broadband induction and exhaust noise: white noise through a bandpass whose centre
 * tracks shaft speed and whose level tracks *load*. Load is what makes a climb sound like work.
 *
 * **Blade slap.** The propeller's own tone — an impulse train at the blade-pass frequency,
 * generated by pushing a sine through a wave-shaper that keeps only the top of each cycle. Loud
 * on a static run-up when the tip is near Mach 0.8 and nothing is masking it, gone by cruise.
 *
 * ## Why a soft clipper
 *
 * Eight clean sine partials sound like a church organ. A real engine is a distorted sound — the
 * exhaust pulses are not sinusoidal and the airframe they arrive through is not linear. A gentle
 * `tanh` saturator on the summed harmonics adds the intermodulation that makes it read as a
 * machine. It is a timbre device; the safety device is the mixer's limiter.
 *
 * `updateFrame(alpha)`: {@link PistonEngineVoice.updateFrame}, once per rendered frame.
 * Allocation-free — every node exists from construction.
 */

import { clamp01 } from '../../core/math.js';
import type { AudioBank } from '../bank.js';
import {
  ENGINE_HARMONIC_COUNT,
  bladePassHz,
  bladeSlapGain,
  engineCycleHz,
  engineLoad,
  firingFundamentalHz,
  harmonicGain,
  idleness,
  intakeCentreHz,
  intakeGain,
  lopeDepth,
  propTipMach,
} from '../model.js';
import { setTarget } from '../param.js';
import type { AircraftSoundConfig } from '../soundSet.js';
import type { AircraftAudioState } from '../state.js';
import {
  bandpass,
  constantSource,
  disconnectAll,
  gainNode,
  highpass,
  lowpass,
  loopingSource,
  waveShaper,
} from './parts.js';

/**
 * Below this RPM the engine is not making a note, only a starter noise.
 *
 * A shaft turning at 40 rpm has a 1.3 Hz fundamental, which is not a pitch — it is a series of
 * separate events, and pretending otherwise produces a subsonic thud that nothing can reproduce
 * and every woofer tries to.
 */
const MIN_TONAL_RPM = 90;

export class PistonEngineVoice {
  private readonly context: AudioContext;
  private readonly config: AircraftSoundConfig;

  /** Carries the firing fundamental [Hz] to every harmonic at once. */
  private readonly fundamental: ConstantSourceNode | null;
  private readonly oscillators: OscillatorNode[] = [];
  private readonly harmonicGains: GainNode[] = [];
  private readonly multipliers: GainNode[] = [];

  private readonly toneSum: GainNode;
  private readonly saturator: WaveShaperNode;

  private readonly intakeSource: AudioBufferSourceNode;
  private readonly intakeBand: BiquadFilterNode;
  private readonly intakeLevel: GainNode;

  private readonly slapSource: AudioBufferSourceNode;
  private readonly slapBand: BiquadFilterNode;
  /** Gain modulated by the blade-pass pulse train; its base value stays at zero. */
  private readonly slapAm: GainNode;
  private readonly slapOscillator: OscillatorNode;
  private readonly slapShaper: WaveShaperNode;
  private readonly slapDepth: GainNode;

  private readonly starterSource: AudioBufferSourceNode;
  private readonly starterBand: BiquadFilterNode;
  private readonly starterWhine: OscillatorNode;
  private readonly starterLevel: GainNode;

  /** The lope: base gain plus a sine at the cycle rate. */
  private readonly lopeAm: GainNode;
  private readonly lopeOscillator: OscillatorNode;
  private readonly lopeDepthGain: GainNode;

  private readonly output: GainNode;

  constructor(
    context: AudioContext,
    bank: AudioBank,
    destination: AudioNode,
    config: AircraftSoundConfig,
  ) {
    this.context = context;
    this.config = config;

    this.output = gainNode(context, 0);
    this.output.connect(destination);

    // --- lope: everything tonal passes through an amplitude modulator at the cycle rate ------
    this.lopeAm = gainNode(context, 1);
    this.lopeAm.connect(this.output);
    this.lopeOscillator = context.createOscillator();
    this.lopeOscillator.type = 'sine';
    this.lopeOscillator.frequency.value = 6;
    this.lopeDepthGain = gainNode(context, 0);
    this.lopeOscillator.connect(this.lopeDepthGain);
    this.lopeDepthGain.connect(this.lopeAm.gain);
    this.lopeOscillator.start(0);

    // --- harmonics ---------------------------------------------------------------------------
    this.saturator = waveShaper(context, bank.softClipCurve);
    this.saturator.connect(this.lopeAm);
    this.toneSum = gainNode(context, 0.22);
    this.toneSum.connect(this.saturator);

    this.fundamental = constantSource(context, 0);
    for (let i = 0; i < ENGINE_HARMONIC_COUNT; i++) {
      const oscillator = context.createOscillator();
      oscillator.type = 'sine';
      /*
       * Base frequency zero: the whole frequency comes in through the parameter connection
       * below. An oscillator whose `frequency.value` is left at the default 440 would sit a
       * fixed 440 Hz above its harmonic, which is audible as an inharmonic ring and is the single
       * easiest mistake to make in this construction.
       */
      oscillator.frequency.value = 0;
      const level = gainNode(context, 0);
      oscillator.connect(level);
      level.connect(this.toneSum);
      oscillator.start(0);

      if (this.fundamental !== null) {
        const multiplier = gainNode(context, i + 1);
        this.fundamental.connect(multiplier);
        multiplier.connect(oscillator.frequency);
        this.multipliers.push(multiplier);
      }
      this.oscillators.push(oscillator);
      this.harmonicGains.push(level);
    }

    // --- intake and exhaust noise ------------------------------------------------------------
    this.intakeLevel = gainNode(context, 0);
    this.intakeLevel.connect(this.lopeAm);
    this.intakeBand = bandpass(context, 500, 0.9);
    this.intakeBand.connect(this.intakeLevel);
    this.intakeSource = loopingSource(context, bank.white, bank.whiteLoopEndS, 1, 0.31);
    this.intakeSource.connect(this.intakeBand);

    // --- blade slap ---------------------------------------------------------------------------
    this.slapDepth = gainNode(context, 0);
    this.slapDepth.connect(this.output);
    this.slapAm = gainNode(context, 0);
    this.slapAm.connect(this.slapDepth);
    this.slapBand = bandpass(context, 1_400, 1.4);
    this.slapBand.connect(this.slapAm);
    // A second read head into the same buffer, at a different offset and a slightly different
    // rate, so the slap layer is not correlated with the intake layer. Two correlated noise
    // sources summed sound like one louder source; two uncorrelated ones sound like two things.
    this.slapSource = loopingSource(context, bank.white, bank.whiteLoopEndS, 1.07, 1.73);
    this.slapSource.connect(this.slapBand);

    this.slapOscillator = context.createOscillator();
    this.slapOscillator.type = 'sine';
    this.slapOscillator.frequency.value = 80;
    this.slapShaper = waveShaper(context, bank.pulseCurve);
    this.slapOscillator.connect(this.slapShaper);
    this.slapShaper.connect(this.slapAm.gain);
    this.slapOscillator.start(0);

    // --- starter ------------------------------------------------------------------------------
    /*
     * A starter is two sounds at once: a fast electric motor whine, and the slow heavy chuff of
     * a cold engine being dragged over compression. The whine is a sawtooth up around 700 Hz;
     * the chuff comes free, because the crank really is turning and the harmonic stack above is
     * already tracking it down at 3–8 Hz.
     */
    this.starterLevel = gainNode(context, 0);
    this.starterLevel.connect(this.output);
    this.starterBand = lowpass(context, 2_400, 1.2);
    this.starterBand.connect(this.starterLevel);
    this.starterWhine = context.createOscillator();
    this.starterWhine.type = 'sawtooth';
    this.starterWhine.frequency.value = 690;
    const starterWhineLevel = gainNode(context, 0.16);
    this.starterWhine.connect(starterWhineLevel);
    starterWhineLevel.connect(this.starterBand);
    this.starterWhine.start(0);
    this.starterSource = loopingSource(context, bank.white, bank.whiteLoopEndS, 0.93, 2.4);
    const starterNoise = highpass(context, 600);
    const starterNoiseLevel = gainNode(context, 0.35);
    this.starterSource.connect(starterNoise);
    starterNoise.connect(starterNoiseLevel);
    starterNoiseLevel.connect(this.starterBand);
  }

  /**
   * Push a frame of engine state.
   *
   * @param state       The aircraft's audio state.
   * @param dopplerRatio Frequency ratio from the listener's motion relative to this aeroplane.
   *                     Applied to the fundamental and to the blade-pass tone, which between them
   *                     are every pitched component of the voice — so the whole engine shifts
   *                     together, exactly as a real one does.
   */
  updateFrame(state: AircraftAudioState, dopplerRatio: number): void {
    const now = this.context.currentTime;
    const config = this.config;
    const rpm = Math.max(0, state.rpm);

    const load = engineLoad(state.powerW, config.ratedPowerW);
    const idle = idleness(rpm, config.idleRpm, config.ratedRpm);
    const rpmFraction = clamp01(rpm / Math.max(1, config.ratedRpm));

    // --- tonal core ---------------------------------------------------------------------------
    const f0 = firingFundamentalHz(rpm, config.cylinders, config.strokes) * dopplerRatio;
    if (this.fundamental !== null) {
      // 12 ms: fast enough that a throttle chop is heard as one, slow enough that the
      // 120 Hz physics tick's own jitter never becomes a warble.
      setTarget(this.fundamental.offset, f0, 0.012, now);
    } else {
      for (let i = 0; i < this.oscillators.length; i++) {
        setTarget(this.oscillators[i].frequency, f0 * (i + 1), 0.012, now);
      }
    }

    // Below the tonal floor the note is faded out rather than detuned to zero, so a shutdown
    // ends in a decaying rumble instead of a subsonic slide.
    const tonal = clamp01((rpm - MIN_TONAL_RPM * 0.5) / MIN_TONAL_RPM);
    for (let i = 0; i < this.harmonicGains.length; i++) {
      setTarget(this.harmonicGains[i].gain, harmonicGain(i, load, idle) * tonal, 0.04, now);
    }

    setTarget(this.lopeOscillator.frequency, engineCycleHz(rpm, config.strokes), 0.05, now);
    const depth = lopeDepth(idle) * tonal;
    // Base + modulator: the gain swings between 1 − depth and 1, so the modulation only ever
    // takes level away. A base of 1 with a bipolar modulator would clip on every up-swing.
    setTarget(this.lopeAm.gain, 1 - depth * 0.5, 0.05, now);
    setTarget(this.lopeDepthGain.gain, depth * 0.5, 0.05, now);

    // --- intake -------------------------------------------------------------------------------
    setTarget(this.intakeBand.frequency, intakeCentreHz(rpm, config.ratedRpm) * dopplerRatio, 0.05, now);
    setTarget(this.intakeLevel.gain, intakeGain(load, rpmFraction) * 0.5 * tonal, 0.05, now);

    // --- blade slap ---------------------------------------------------------------------------
    const bladeHz = bladePassHz(rpm, config.bladeCount) * dopplerRatio;
    setTarget(this.slapOscillator.frequency, Math.max(1, bladeHz), 0.02, now);
    setTarget(this.slapBand.frequency, clampHz(bladeHz * 16), 0.06, now);
    const tipMach = propTipMach(rpm, config.propDiameterM, state.trueAirspeedMps, state.speedOfSoundMps);
    setTarget(
      this.slapDepth.gain,
      bladeSlapGain(tipMach, state.indicatedAirspeedMps) * 0.55 * tonal,
      0.07,
      now,
    );

    // --- starter -------------------------------------------------------------------------------
    setTarget(this.starterLevel.gain, state.starterEngaged ? 0.5 : 0, 0.05, now);
    setTarget(this.starterWhine.frequency, 690 * dopplerRatio, 0.05, now);

    // --- the whole voice ------------------------------------------------------------------------
    /*
     * One overall gate on RPM presence. Two things depend on it: a shut-down engine is silent
     * (and the birds can be heard), and the *catch* — the instant the starter lets go and the
     * engine picks up — is a real audible event rather than a crossfade, because the harmonic
     * stack goes from a cranking 200 rpm to a firing 700 in about a second and this gain follows
     * it up.
     */
    const presence = clamp01(rpm / Math.max(60, config.idleRpm * 0.6));
    setTarget(this.output.gain, presence * config.engineTrim, 0.06, now);
  }

  dispose(): void {
    for (const oscillator of this.oscillators) oscillator.stop();
    this.lopeOscillator.stop();
    this.slapOscillator.stop();
    this.starterWhine.stop();
    this.intakeSource.stop();
    this.slapSource.stop();
    this.starterSource.stop();
    this.fundamental?.stop();
    disconnectAll([
      ...this.oscillators,
      ...this.harmonicGains,
      ...this.multipliers,
      this.fundamental,
      this.toneSum,
      this.saturator,
      this.intakeSource,
      this.intakeBand,
      this.intakeLevel,
      this.slapSource,
      this.slapBand,
      this.slapAm,
      this.slapOscillator,
      this.slapShaper,
      this.slapDepth,
      this.starterSource,
      this.starterBand,
      this.starterWhine,
      this.starterLevel,
      this.lopeAm,
      this.lopeOscillator,
      this.lopeDepthGain,
      this.output,
    ]);
  }
}

/** Keep a filter corner inside the audible band, whatever the arithmetic produced. */
function clampHz(hz: number): number {
  return hz < 40 ? 40 : hz > 18_000 ? 18_000 : hz;
}
